import type { FunctionArgs } from "convex/server"
import type { Id, Priority, api } from "@/lib/api"
import { isWellFormedText } from "../../../../convex/lib/feedbackPayload"
import { MAX_FILTER_VALUES } from "../../../../convex/lib/limits"
import { UNASSIGNED } from "../../../../convex/lib/validators"

/**
 * The Issues page as the URL holds it: which view is open and what narrows
 * it, and the way from there to what the server takes. Pure, so every rule
 * is tested directly: the URL is the only place this lives, and a link
 * someone shares, a reload and the back button all have to give the same
 * page.
 */

/** The views of the page. The list is the one the bare address opens. */
export const ISSUE_VIEWS = ["list", "board", "milestones"] as const
export type IssueView = (typeof ISSUE_VIEWS)[number]

const PRIORITIES = [
  "none",
  "low",
  "medium",
  "high",
  "urgent",
] as const satisfies readonly Priority[]

/** In `assignee`, the value for issues assigned to nobody. */
export const NO_ASSIGNEE = UNASSIGNED
/** In `milestone` and `type`, the value for issues that have none. */
export const NO_VALUE = "none"

/** What `issues.list` takes as `filters`. */
export type IssueFilters = FunctionArgs<typeof api.issues.list>["filters"]

/**
 * The search params of the Issues page. Every key is optional and none is
 * ever an empty list. An issue has one milestone and one type, and the
 * server filters on one of each: those two hold a single value.
 */
export type IssuesSearch = {
  /** Left out for the list. */
  view?: Exclude<IssueView, "list">
  /** User ids, and `NO_ASSIGNEE`. */
  assignee?: string[]
  priority?: Priority[]
  /** Label ids. */
  label?: string[]
  /** A milestone id, or `NO_VALUE`. */
  milestone?: string
  /** An issue type id, or `NO_VALUE`. */
  type?: string
}

// What the server takes in an assignee filter: `cleanIssueFilters` in
// convex/lib/issueFilters.ts refuses the whole list for one value outside this.
const USER_ID_MAX = 128
const isUserId = (value: string) =>
  value.length > 0 && value.length <= USER_ID_MAX && isWellFormedText(value)

// A Convex document id is lowercase letters and digits. Whether it names a
// label or a milestone only the server can say, but what could not be an id
// at all never reaches it.
const DOCUMENT_ID = /^[a-z0-9]{16,64}$/
const isDocumentId = (value: string) => DOCUMENT_ID.test(value)

/**
 * A list as the URL may carry it: an array, one value, or values between
 * commas for a link written by hand. Anything that is not an accepted value
 * is left out, as is a repeat and whatever comes after the most the server
 * takes of one kind.
 */
function listOf<Value extends string>(
  raw: unknown,
  accepts: (value: string) => boolean,
): Value[] | undefined {
  const candidates: unknown[] =
    typeof raw === "string" ? raw.split(",") : Array.isArray(raw) ? raw : []
  const kept: string[] = []
  for (const candidate of candidates) {
    if (typeof candidate !== "string") continue
    const value = candidate.trim()
    if (!accepts(value) || kept.includes(value)) continue
    kept.push(value)
    if (kept.length === MAX_FILTER_VALUES) break
  }
  return kept.length > 0 ? (kept as Value[]) : undefined
}

/** One id, or the word for "none". */
function oneOf(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined
  const value = raw.trim()
  return value === NO_VALUE || isDocumentId(value) ? value : undefined
}

/**
 * `validateSearch` of the route. Whatever the URL says, this returns a page
 * that can be drawn: an unknown key or value is dropped, never thrown on.
 */
export function parseIssuesSearch(raw: Record<string, unknown>): IssuesSearch {
  const search: IssuesSearch = {}
  if (raw.view === "board" || raw.view === "milestones") search.view = raw.view
  const assignee = listOf<string>(raw.assignee, isUserId)
  if (assignee) search.assignee = assignee
  const given = listOf<Priority>(raw.priority, (value) =>
    (PRIORITIES as readonly string[]).includes(value),
  )
  // In the set's own order, whatever order they came in.
  if (given) search.priority = PRIORITIES.filter((priority) => given.includes(priority))
  const label = listOf<string>(raw.label, isDocumentId)
  if (label) search.label = label
  const milestone = oneOf(raw.milestone)
  if (milestone) search.milestone = milestone
  const type = oneOf(raw.type)
  if (type) search.type = type
  return search
}

/** The `filters` argument of `issues.list` for a URL. Keys are always in this order. */
export function filtersOf(search: IssuesSearch): IssueFilters {
  const filters: IssueFilters = {}
  if (search.assignee) filters.assigneeId = search.assignee
  if (search.priority) filters.priority = search.priority
  if (search.label) filters.labelId = search.label as Id<"labels">[]
  if (search.milestone) {
    filters.milestoneId =
      search.milestone === NO_VALUE ? null : (search.milestone as Id<"milestones">)
  }
  if (search.type) {
    filters.typeId = search.type === NO_VALUE ? null : (search.type as Id<"issueTypes">)
  }
  return filters
}

/** Whether the filters narrow the list at all. */
export function hasFilters(filters: IssueFilters): boolean {
  return Object.values(filters).some((value) =>
    Array.isArray(value) ? value.length > 0 : value !== undefined,
  )
}

/** The ids `<FilterBar>` knows the filters by. They are the search params' own names. */
export type FilterId = Exclude<keyof IssuesSearch, "view">

/** The URL as `<FilterBar>` takes it: every kind a list, the single ones a list of one. */
export function filterValueOf(search: IssuesSearch): Record<string, string[]> {
  const value: Record<string, string[]> = {}
  if (search.assignee) value.assignee = search.assignee
  if (search.priority) value.priority = search.priority
  if (search.label) value.label = search.label
  if (search.milestone) value.milestone = [search.milestone]
  if (search.type) value.type = [search.type]
  return value
}

/**
 * And back: what `<FilterBar>` reports, as search params, in the view that
 * is open. Its pickers tick several values; of a milestone or a type the one
 * ticked last is kept, so picking another replaces the first.
 */
export function searchOfFilterValue(
  value: Record<string, string[]>,
  view?: IssuesSearch["view"],
): IssuesSearch {
  return parseIssuesSearch({
    ...value,
    view,
    milestone: value.milestone?.at(-1),
    type: value.type?.at(-1),
  })
}
